import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SwipeStack } from '@/components/swipe/SwipeStack';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Heart, ArrowLeft, Sparkles, Filter, DollarSign } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';

type Listing = Database['public']['Views']['v_listings_with_image']['Row'] & { price: number | null };

// De-duplicate by listing_number (digits only), falling back to title+sector+location
const dedupeListings = (arr: Listing[]) => {
  const seen = new Set<string>();
  const out: Listing[] = [];
  for (const l of arr || []) {
    const num = (l.listing_number || '').replace(/[^0-9]/g, '');
    const fallbackKey = `${(l.title || '').trim()}|${(l.sector || '').trim()}|${(l.location_suburb || '').trim()}|${(l.location_state || '').trim()}`;
    const key = (num || fallbackKey).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(l);
  }
  return out;
};

export default function Swipe() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(0);
  const [sectorFilter, setSectorFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [availableSectors, setAvailableSectors] = useState<string[]>([]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchListings();
    fetchFilterOptions();
  }, [user, sectorFilter, stateFilter, minPrice, maxPrice, sortBy]);

  const fetchFilterOptions = async () => {
    try {
      // Fetch unique sectors
      const { data: sectorsData } = await supabase
        .from('listings')
        .select('sector')
        .eq('status', 'live')
        .not('sector', 'is', null);

      const uniqueSectors = Array.from(new Set(sectorsData?.map(item => item.sector) || []));
      setAvailableSectors(uniqueSectors);

      // Fetch unique states
      const { data: statesData } = await supabase
        .from('listings')
        .select('location_state')
        .eq('status', 'live')
        .not('location_state', 'is', null);

      const uniqueStates = Array.from(new Set(statesData?.map(item => item.location_state) || []));
      setAvailableStates(uniqueStates);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchListings = async () => {
    try {
      setListings([]); // Reset listings when filters change
      setLastFetched(0);
      
      let query = supabase
        .from('v_listings_with_image')
        .select('*')
        .eq('status', 'live');

      // Apply sector filter
      if (sectorFilter !== 'all') {
        query = query.eq('sector', sectorFilter);
      }

      // Apply state filter  
      if (stateFilter !== 'all') {
        query = query.eq('location_state', stateFilter);
      }

      // Apply price filters
      if (minPrice) {
        const minPriceNum = parseInt(minPrice);
        query = query.gte('price', minPriceNum);
      }

      if (maxPrice) {
        const maxPriceNum = parseInt(maxPrice);
        query = query.lte('price', maxPriceNum);
      }

      // Apply sorting
      switch (sortBy) {
        case 'price-low':
          query = query
            .order('price', { ascending: true, nullsFirst: false })
            .order('created_at', { ascending: false })
            .order('id', { ascending: false });
          break;
        case 'price-high':
          query = query
            .order('price', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false })
            .order('id', { ascending: false });
          break;
        case 'newest':
        default:
          query = query
            .order('created_at', { ascending: false })
            .order('id', { ascending: false });
          break;
      }

      query = query.limit(20);

      // If user is logged in, exclude their favorites
      if (user) {
        const { data: favorites } = await supabase
          .from('favorites')
          .select('listing_id')
          .eq('user_id', user.id);

        if (favorites && favorites.length > 0) {
          const favoriteIds = favorites.map(f => f.listing_id);
          query = query.not('id', 'in', `(${favoriteIds.join(',')})`);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      setListings(dedupeListings(data || []));
      setLastFetched(data?.length || 0);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNeedMoreListings = async () => {
    if (loading) return;
    
    try {
      let query = supabase
        .from('v_listings_with_image')
        .select('*')
        .eq('status', 'live');

      // Apply same filters as initial fetch
      if (sectorFilter !== 'all') {
        query = query.eq('sector', sectorFilter);
      }
      if (stateFilter !== 'all') {
        query = query.eq('location_state', stateFilter);
      }

      // Apply price filters
      if (minPrice) {
        const minPriceNum = parseInt(minPrice);
        query = query.gte('price', minPriceNum);
      }

      if (maxPrice) {
        const maxPriceNum = parseInt(maxPrice);
        query = query.lte('price', maxPriceNum);
      }

      // Apply sorting
      switch (sortBy) {
        case 'price-low':
          query = query
            .order('price', { ascending: true, nullsFirst: false })
            .order('created_at', { ascending: false })
            .order('id', { ascending: false });
          break;
        case 'price-high':
          query = query
            .order('price', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false })
            .order('id', { ascending: false });
          break;
        case 'newest':
        default:
          query = query
            .order('created_at', { ascending: false })
            .order('id', { ascending: false });
          break;
      }

      // If user is logged in, exclude their favorites
      if (user) {
        const { data: favorites } = await supabase
          .from('favorites')
          .select('listing_id')
          .eq('user_id', user.id);

        if (favorites && favorites.length > 0) {
          const favoriteIds = favorites.map(f => f.listing_id);
          query = query.not('id', 'in', `(${favoriteIds.join(',')})`);
        }
      }

      // Offset by already fetched listings
      query = query.range(lastFetched, lastFetched + 19);

      const { data, error } = await query;
      if (error) throw error;

      setListings(prev => dedupeListings([...(prev || []), ...(data || [])]));
      setLastFetched(prev => prev + (data?.length || 0));
    } catch (error) {
      console.error('Error fetching more listings:', error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-muted-foreground" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Sign In Required</h2>
              <p className="text-muted-foreground mb-6">
                Please sign in to use swipe mode and save your favorites.
              </p>
              <div className="space-x-4">
                <Button asChild>
                  <Link to="/sign-in">Sign In</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/explore">Browse Without Account</Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading && listings.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Loading businesses...</h3>
            <p className="text-muted-foreground">Finding the perfect matches for you</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button variant="outline" size="sm" asChild className="w-fit">
              <Link to="/explore">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Browse
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                <Sparkles className="h-6 md:h-8 w-6 md:w-8 text-primary" />
                Swipe Mode
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Discover businesses the fun way
              </p>
            </div>
          </div>
          
          <Button variant="outline" asChild className="w-fit">
            <Link to="/favorites">
              <Heart className="h-4 w-4 mr-2 text-red-500 fill-current" />
              <span className="hidden sm:inline">My Favorites</span>
              <span className="sm:hidden">Favorites</span>
            </Link>
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="max-w-4xl mx-auto bg-muted/50 rounded-lg p-4 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-5 w-5 text-primary" />
              <h3 className="text-sm md:text-base font-semibold">Filter Your Matches</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Industry Sector
                </label>
                <Select value={sectorFilter} onValueChange={setSectorFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All sectors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sectors</SelectItem>
                    {availableSectors.map((sector) => (
                      <SelectItem key={sector} value={sector}>
                        {sector}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Location (State)
                </label>
                <Select value={stateFilter} onValueChange={setStateFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All states</SelectItem>
                    {availableStates.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Price Range
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="Min"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  <div className="relative flex-1">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Sort By
                </label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-low">Price: Low → High</SelectItem>
                    <SelectItem value="price-high">Price: High → Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="text-center mb-8">
          <div className="max-w-2xl mx-auto bg-muted/50 rounded-lg p-4 md:p-6">
            <h3 className="text-sm md:text-base font-semibold mb-2">How it works</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <Heart className="h-3 w-3 text-green-600 fill-current" />
                </div>
                <span>Swipe right or tap ❤️ to add to favorites</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 text-xs">✕</span>
                </div>
                <span>Swipe left or tap ✕ to pass</span>
              </div>
            </div>
          </div>
        </div>

        {/* Swipe Interface */}
        <SwipeStack 
          listings={listings}
          onNeedMoreListings={handleNeedMoreListings}
        />
      </main>
      <Footer />
    </div>
  );
}